import { useEffect } from "react";
import { useRouter } from "@tanstack/react-router";
import { listPublicProducts, listPublicRecipes } from "@/lib/public.functions";

type ToolResult = { content: Array<{ type: "text"; text: string }> };

type WebMcpTool = {
  name: string;
  description: string;
  inputSchema: Record<string, unknown>;
  execute: (args: any) => Promise<ToolResult>;
};

const SHOP_INFO = {
  name: "La Bottega delle Carni da Marco",
  descrizione:
    "Bottega artigianale di pregiate carni a Campobasso (Molise), dentro il supermercato Tigre di Via Kennedy 1. Carni frollate, salumi tradizionali e produzione de L'Abatina (Slow Food, De.Co. Campobasso).",
  indirizzo: "Via John Fitzgerald Kennedy 1, 86100 Campobasso (CB), Italia",
  telefono: "+39 340 748 7310",
  orari: "Lunedì–Sabato 8:00–13:30 e 16:00–20:00. Domenica chiuso (ritiro su prenotazione).",
  spedizioni: "Nessuna spedizione: solo ritiro in bottega.",
  partitaIva: "IT01695340701",
  pagine: ["/", "/chi-siamo", "/abatina", "/prodotti", "/ricette", "/richieste", "/contatti"],
};

function text(value: unknown): ToolResult {
  return {
    content: [{ type: "text", text: typeof value === "string" ? value : JSON.stringify(value) }],
  };
}

export function WebMcpTools() {
  const router = useRouter();

  useEffect(() => {
    const ctx = (navigator as any)?.modelContext;
    if (!ctx || typeof ctx.registerTool !== "function") return;

    const tools: WebMcpTool[] = [
      {
        name: "get_shop_info",
        description:
          "Restituisce indirizzo, orari, telefono e informazioni sulla macelleria La Bottega delle Carni da Marco a Campobasso.",
        inputSchema: { type: "object", properties: {}, additionalProperties: false },
        execute: async () => text(SHOP_INFO),
      },
      {
        name: "list_products",
        description:
          "Elenca i prodotti disponibili in bottega (carni, preparati, salumi) con nome, slug, categoria e prezzo.",
        inputSchema: { type: "object", properties: {}, additionalProperties: false },
        execute: async () => {
          const { products } = await listPublicProducts();
          return text(
            (products ?? []).map((p: any) => ({
              nome: p.name,
              slug: p.slug,
              categoria: p.category ?? null,
              prezzo: p.price ? `${p.price} €` : "Prezzo su richiesta",
              url: `/prodotti/${p.slug}`,
            })),
          );
        },
      },
      {
        name: "list_recipes",
        description: "Elenca le ricette pubblicate dalla bottega con titolo e indirizzo di pagina.",
        inputSchema: { type: "object", properties: {}, additionalProperties: false },
        execute: async () => {
          const { recipes } = await listPublicRecipes();
          return text(
            (recipes ?? []).map((r: any) => ({
              titolo: r.title,
              slug: r.slug,
              url: `/ricette/${r.slug}`,
            })),
          );
        },
      },
      {
        name: "navigate_to_page",
        description:
          "Apre una pagina del sito. Percorsi validi: /, /chi-siamo, /abatina, /prodotti, /ricette, /richieste, /contatti.",
        inputSchema: {
          type: "object",
          properties: { path: { type: "string", description: "Percorso interno, es. /prodotti" } },
          required: ["path"],
          additionalProperties: false,
        },
        execute: async ({ path }: { path: string }) => {
          if (!path?.startsWith("/")) return text("Percorso non valido.");
          await router.navigate({ href: path });
          return text(`Aperta la pagina ${path}.`);
        },
      },
    ];

    const registrations = tools.map((tool) => {
      try {
        return ctx.registerTool(tool);
      } catch {
        return null;
      }
    });

    return () => {
      registrations.forEach((reg: any) => {
        try {
          reg?.unregister?.();
        } catch {
          /* noop */
        }
      });
    };
  }, [router]);

  return null;
}
